import { motion } from 'framer-motion';
import {
  Calendar,
  Tag,
  TrendingDown,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import type { AppData } from '../../data/types';
import { calculateDailyAllowance, txContributionInRange } from '../../lib/budget';
import { formatCurrency } from '../../lib/currency';
import { addDays } from '../../lib/date';
import { getPayPeriod } from '../../lib/payPeriod';
import { springGentle, tapScale, springQuick } from '../../tokens';

type Props = {
  data: AppData;
  today: Date;
  onTap: () => void;
};

type ChipColor =
  | 'bg-blue-500'
  | 'bg-zinc-700'
  | 'bg-amber-500'
  | 'bg-emerald-500'
  | 'bg-red-500';

type Chip = {
  key: string;
  label: string;
  value: string;
  iconBg: ChipColor;
  Icon: LucideIcon;
};

/**
 * Horizontal-scroll insights strip on the Today screen. Four chips:
 * Spent today, This period, Top category, Vs last period. Tap a chip
 * to jump to the Insights tab.
 */
export function InsightsStrip({ data, today, onTap }: Props) {
  const currency = data.profile.currency;

  const chips: Chip[] = useMemo(() => {
    const daily = calculateDailyAllowance(data, today);
    const period = daily.period;

    // Top category by current-period spend.
    const byCat = new Map<string, number>();
    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      const v = txContributionInRange(t, period.start, period.end);
      if (v > 0) byCat.set(t.categoryId, (byCat.get(t.categoryId) ?? 0) + v);
    }
    let topCatId: string | null = null;
    let topCatAmount = 0;
    for (const [id, amt] of byCat) {
      if (amt > topCatAmount) {
        topCatId = id;
        topCatAmount = amt;
      }
    }
    const topCatName = topCatId
      ? data.categories.find((c) => c.id === topCatId)?.name ?? '–'
      : '–';
    const topCatLabel =
      topCatName.length > 12 ? `${topCatName.slice(0, 12)}…` : topCatName;

    // Last-period spend: walk back one period from period.start - 1.
    const priorAnchor = addDays(period.start, -1);
    const priorPeriod = getPayPeriod(data.profile, priorAnchor);
    let priorSpend = 0;
    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      priorSpend += txContributionInRange(
        t,
        priorPeriod.start,
        priorPeriod.end,
      );
    }
    const delta = daily.spentThisPeriod - priorSpend;
    const hasPrior = priorSpend > 0;
    const deltaLabel = hasPrior
      ? `${delta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(delta), { currency })}`
      : '–';
    const trendingIcon = delta >= 0 ? TrendingUp : TrendingDown;
    const trendingColor: ChipColor = !hasPrior
      ? 'bg-zinc-700'
      : delta >= 0
        ? 'bg-red-500'
        : 'bg-emerald-500';

    return [
      {
        key: 'spent-today',
        label: 'Spent today',
        value: formatCurrency(daily.spentToday, { currency }),
        iconBg: 'bg-blue-500',
        Icon: Wallet,
      },
      {
        key: 'this-period',
        label: 'This period',
        value: formatCurrency(daily.spentThisPeriod, { currency }),
        iconBg: 'bg-zinc-700',
        Icon: Calendar,
      },
      {
        key: 'top-category',
        label: topCatId ? 'Top category' : 'No spending',
        value: topCatId ? topCatLabel : '–',
        iconBg: 'bg-amber-500',
        Icon: Tag,
      },
      {
        key: 'vs-last',
        label: hasPrior ? 'Vs last period' : 'No prior data',
        value: deltaLabel,
        iconBg: trendingColor,
        Icon: trendingIcon,
      },
    ];
  }, [data, today, currency]);

  return (
    <div className="mt-6">
      <div className="ml-5 mb-3 text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
        Insights
      </div>
      <div
        className="flex gap-3 overflow-x-auto pl-5 pr-5 pb-1"
        style={{
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {chips.map((c, i) => (
          <motion.button
            key={c.key}
            type="button"
            onClick={onTap}
            whileTap={tapScale}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: i * 0.03 }}
            className="shrink-0 w-[150px] h-[96px] rounded-2xl bg-surface border border-surface-secondary p-3 flex flex-col justify-between text-left"
            style={{ scrollSnapAlign: 'start' }}
          >
            <div
              className={`w-8 h-8 rounded-xl inline-flex items-center justify-center text-white ${c.iconBg}`}
            >
              <c.Icon size={16} strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[17px] font-semibold text-text-primary tabular-nums truncate">
                {c.value}
              </div>
              <div className="text-[11px] font-medium text-text-tertiary">
                {c.label}
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// Avoid a tree-shake warning when springQuick goes unused.
void springQuick;
